import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { SubscriptionModal } from "../Subscription/SubscriptionModal";
import { cn } from "../../utils/cn";
import { useAuthStore } from "../../store/authStore";
import { useGetStarted } from "./hooks/useGetStarted";
import { useSubscriptionStore } from "../../store/subscriptionStore";

const navigation = [
  { name: "Features", href: "#features" },
  { name: "Pricing", href: "#pricing" },
  { name: "Testimonials", href: "#testimonials" },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { handleGetStarted, showSubscriptionModal, setShowSubscriptionModal, isLoading } =
    useGetStarted();
  const user = useAuthStore((state) => state.user);
  const { isSubscribed } = useSubscriptionStore();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-[100] transition-all duration-500",
          isScrolled ? "py-3" : "py-5"
        )}
      >
        {/* Backdrop blur container */}
        <div
          className={cn(
            "absolute inset-0 transition-all duration-500",
            isScrolled
              ? "bg-midnight-950/80 backdrop-blur-xl border-b border-midnight-800/50"
              : "bg-transparent"
          )}
        />

        <nav className="relative max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            className="flex items-center gap-3 group"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#hero");
            }}
          >
            {/* Logo icon */}
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-coral-500 to-coral-600 flex items-center justify-center shadow-glow transition-all duration-300 group-hover:shadow-glow-lg group-hover:scale-105">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {/* Pulse indicator */}
              <div className="absolute -top-0.5 -right-0.5 w-3 h-3">
                <span className="absolute inset-0 rounded-full bg-mint-500 animate-ping opacity-75" />
                <span className="absolute inset-0 rounded-full bg-mint-500" />
              </div>
            </div>
            
            {/* Logo text */}
            <span className="text-xl font-bold text-white tracking-tight">
              FeedbackFlow
            </span>
          </a>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className="relative px-4 py-2 text-sm font-medium text-midnight-300 hover:text-white transition-colors duration-200 group"
              >
                {item.name}
                {/* Hover underline */}
                <span className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-coral-500 to-coral-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {!user && (
              <a
                href="/?mode=signin"
                className="text-sm font-medium text-midnight-300 hover:text-white transition-colors"
              >
                Sign in
              </a>
            )}
            
            <button
              onClick={handleGetStarted}
              disabled={isLoading}
              className={cn(
                "relative group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300",
                "bg-coral-500 text-white",
                "hover:bg-coral-400 hover:shadow-glow",
                "active:scale-[0.98]",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {/* Shimmer effect */}
              <span className="absolute inset-0 rounded-xl overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </span>
              
              <span className="relative flex items-center gap-2">
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {!user
                      ? "Get Started"
                      : !isSubscribed
                      ? "Upgrade"
                      : "Dashboard"}
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg text-midnight-300 hover:text-white hover:bg-midnight-800/50 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open menu</span>
            <Bars3Icon className="w-6 h-6" />
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        {/* Backdrop */}
        <div className="fixed inset-0 z-[150] bg-midnight-950/80 backdrop-blur-sm" />
        
        {/* Panel */}
        <Dialog.Panel className="fixed inset-y-0 right-0 z-[150] w-full max-w-sm overflow-y-auto bg-midnight-900/95 backdrop-blur-xl border-l border-midnight-800/50">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-midnight-800/50">
            <a href="#hero" className="flex items-center gap-3" onClick={(e) => { e.preventDefault(); handleNavClick("#hero"); }}>
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-coral-500 to-coral-600 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="text-lg font-bold text-white">FeedbackFlow</span>
            </a>
            
            <button
              type="button"
              className="p-2 rounded-lg text-midnight-400 hover:text-white hover:bg-midnight-800/50 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <div className="p-6 space-y-2">
            {navigation.map((item, index) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl text-midnight-200 hover:text-white hover:bg-midnight-800/50 transition-all duration-200 opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500" />
                <span className="font-medium">{item.name}</span>
              </a>
            ))}
          </div>

          {/* CTA section */}
          <div className="p-6 border-t border-midnight-800/50 space-y-4">
            {!user && (
              <a
                href="/?mode=signin"
                className="block w-full px-4 py-3 rounded-xl text-center font-medium text-midnight-200 hover:text-white border border-midnight-700 hover:border-midnight-600 transition-all"
              >
                Sign in
              </a>
            )}
            
            <button
              onClick={handleGetStarted}
              disabled={isLoading}
              className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-coral-500 hover:bg-coral-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {!user ? "Get Started Free" : !isSubscribed ? "Upgrade Now" : "Go to Dashboard"}
                  <ArrowRightIcon className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Features list */}
            <div className="pt-4 space-y-3">
              {[
                { icon: "✓", text: "14-day free trial" },
                { icon: "✓", text: "No credit card required" },
                { icon: "✓", text: "Setup in 5 minutes" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-midnight-400">
                  <span className="text-mint-500">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </>
  );
};
